<template>
  <div class="grid gap-20">
    <p>
      <InlineMarkdown :message="translations.intro" />
    </p>

    <div class="grid gap-10 md:grid-cols-3 md:gap-20">
      <section
        v-for="stat in stats"
        :key="stat.id"
        class="rounded-md grid content-start gap-12 bg-[--raw-black] p-16 text-[--raw-offwhite]"
      >
        <!-- Tight tags: whitespace here would double the authored space. -->
        <p class="text-[48px] font-[600] leading-[1]">
          {{ stat.figure.value
          }}<span class="text-[20px]">{{ stat.figure.unit }}</span>
        </p>
        <p class="raw-body-3 font-[500] text-[--raw-lightgrey]">
          <InlineMarkdown :message="stat.body" />
        </p>
      </section>
    </div>

    <div class="grid gap-10 md:grid-cols-2 md:gap-20">
      <section
        v-for="card in cards"
        :key="card.id"
        class="rounded-md grid grid-rows-[1fr_auto] gap-24 bg-[white]/60 p-24"
      >
        <div class="grid content-start gap-12">
          <h3 class="raw-heading-2 text-balance">
            {{ i18n(card.title) }}
          </h3>
          <p class="text-[--raw-verydarkgrey]">
            <InlineMarkdown :message="card.body" />
          </p>
        </div>

        <!-- Outbound is a link; a card of ours is a button. -->
        <component
          :is="card.open ? 'button' : 'a'"
          v-bind="ctaAttrs(card)"
          class="raw-cta-3 rounded-full flex h-36 w-full cursor-pointer items-center justify-center bg-[white] transition-colors duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] hover:bg-[color-mix(in_oklab,var(--raw-lightgrey)_75%,transparent)] active:bg-[color-mix(in_oklab,var(--raw-lightgrey)_75%,transparent)]"
          @click="handleClick(card)"
        >
          {{ i18n(card.cta) }}
        </component>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import InlineMarkdown from '~/scopes/campaign/components/UI/InlineMarkdown.vue'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import type { I18nDescriptor } from '~/scopes/campaign/data/metals/types'

import translations from './TakeActionContent.translations'

const i18n = useI18n()
const route = useRoute()
const { trackClick } = useTracking()
const { openMpLetter } = useModalState()

const UK_LOCALE = 'en-gb'

// Route param, not `useI18nLocale()`: that reads a module-level ref once, and this component
// stays mounted across a locale switch.
const isUk = computed(() => {
  const param = route.params.locale

  return (Array.isArray(param) ? param[0] : param) === UK_LOCALE
})

// Leading number is the value, the rest the unit; ending on a digit keeps ' billion' out
// of the value while '1 500' holds together.
const FIGURE = /^([\d.,\s]*\d)(.*)$/

function splitFigure(message: I18nDescriptor) {
  const text = i18n(message)
  const [, value = text, unit = ''] = text.match(FIGURE) ?? []

  return { value, unit }
}

// Computed because the split resolves copy, so it has to re-run with the locale.
const stats = computed(() => [
  {
    id: 'production',
    figure: splitFigure(translations.stat1Figure),
    body: translations.stat1Body,
  },
  {
    id: 'footprint',
    figure: splitFigure(translations.stat2Figure),
    body: translations.stat2Body,
  },
  {
    id: 'materials',
    figure: splitFigure(translations.stat3Figure),
    body: translations.stat3Body,
  },
])

// A union, so `ctaAttrs` narrows to a defined `url` with no assertion and no dead fallback.
type TakeActionCard = {
  id: string
  title: I18nDescriptor
  body: I18nDescriptor
  cta: I18nDescriptor
} & (
  | { url: I18nDescriptor; open?: undefined }
  | { url?: undefined; open: () => void }
)

// Computed because what the petition card *does* is per market. Only the behaviour branches
// here; its label, title and body are Lokalise's to give an `en-gb` value.
const cards = computed<TakeActionCard[]>(() => [
  {
    id: 'lifespan',
    title: translations.card1Title,
    body: translations.card1Body,
    cta: translations.card1Cta,
    url: translations.card1Url,
  },
  {
    id: 'petition',
    title: translations.card2Title,
    body: translations.card2Body,
    cta: translations.card2Cta,
    ...(isUk.value ? { open: openMpLetter } : { url: translations.card2Url }),
  },
])

// `<component :is>` keeps the CTA's long `class` a single static attribute, so it is neither
// duplicated nor hidden from `tailwindcss/no-custom-classname`.
function ctaAttrs(card: TakeActionCard) {
  if (card.open) return { type: 'button' }

  return { href: i18n(card.url), target: '_blank', rel: 'noopener' }
}

function handleClick(card: TakeActionCard) {
  trackClick({ zone: 'take-action', name: card.id })
  card.open?.()
}
</script>
